import { AlertTriangle, CheckCircle2, RotateCcw, Image as ImageIcon } from 'lucide-react';
import { useState } from 'react';
import { Badge, Modal, ModalHeader } from './_ui';
import { useRenvoyerDeclaration } from '@/hooks/useTerrainAdmin';

/**
 * Carte d'une déclaration terrain en attente de validation.
 *
 * Partagée par l'écran « Validations en attente » du DAF et la page du back-
 * office terrain : deux vues, une seule façon de présenter ce qui est déclaré.
 * La dupliquer ferait diverger les deux, et le DAF finirait par voir moins
 * d'informations à un endroit qu'à l'autre.
 */
export const TYPES_LABEL: Record<string, string> = {
  production:   'Production',
  reception_mp: 'Réception MP',
  sortie_parc:  'Sortie parc',
  etat_stock:   'État des stocks',
  rebut:        'Rebut / casse',
};

export function DeclarationValidationCard({ declaration: d, onValider, onRenvoyer, pending }: {
  declaration: any;
  onValider: () => void;
  onRenvoyer: () => void;
  pending: boolean;
}) {
  const lignes: any[] = Array.isArray(d.lignes) ? d.lignes : [];
  return (
    <div className="ds-panel" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 12 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>
            {TYPES_LABEL[d.type] ?? d.type}
          </div>
          <div style={{ fontSize: 13, color: 'var(--tx-2)', marginTop: 4 }}>
            {d.intervenant?.nom ?? '—'} · {d.site?.code ?? '—'}
            {' · '}Activité <strong>{formatJour(d.dateActivite)}</strong>
            {d.soumiseLe && <> · soumise {formatDateTime(d.soumiseLe)}</>}
          </div>
        </div>
        {d.horsDelai && (
          <span title="Soumise après le cutoff J+1 10h UTC">
            <Badge tone="warning"><AlertTriangle size={11} style={{ verticalAlign: 'middle' }} /> Hors délai</Badge>
          </span>
        )}
      </div>

      {/* Ce qui a été déclaré — et ce que l'outil en pense.

          Le DAF voyait « PBA 9-650 · 4 U » et devait trancher là-dessus. Un
          comptage ne veut rien dire seul : ce qui compte, c'est l'écart avec
          le théorique. Quatre unités, c'est normal si l'outil en annonce
          quatre, c'est un incident s'il en annonce douze. */}
      <div style={{ background: 'var(--bg-2)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
        {lignes.length === 0 && <div style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucune ligne.</div>}
        {lignes.map((l, i) => {
          const ecart = typeof l.ecart === 'number' ? l.ecart : null;
          const couleurEcart = ecart === null ? undefined : ecart === 0 ? 'var(--ok-1)' : 'var(--danger)';
          return (
            <div key={i} style={{ padding: '8px 0', borderTop: i > 0 ? '1px solid var(--bd)' : undefined }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{l.libelle ?? l.articleCode}</span>
                  {l.libelle && (
                    <span style={{ fontSize: 11, color: 'var(--tx-3)', marginLeft: 6 }}>{l.articleCode}</span>
                  )}
                </div>
                <div style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 15, whiteSpace: 'nowrap' }}>
                  {formatQuantite(l.quantite)} {l.unite}
                </div>
              </div>

              {/* Comparaison au théorique — le cœur de la décision */}
              {ecart !== null && (
                <div style={{ display: 'flex', gap: 16, marginTop: 4, fontSize: 12, alignItems: 'center' }}>
                  <span style={{ color: 'var(--tx-3)' }}>
                    {l.source === 'parc' ? 'Au parc' : 'En stock'} selon l'outil :{' '}
                    <strong style={{ color: 'var(--tx-1)' }}>{l.stockTheorique}</strong>
                  </span>
                  <span style={{ color: couleurEcart, fontWeight: 700 }}>
                    {ecart === 0 ? 'Conforme' : `Écart ${ecart > 0 ? '+' : ''}${ecart}`}
                  </span>
                  {ecart !== 0 && (
                    <span style={{ color: 'var(--tx-3)' }}>
                      {ecart < 0 ? 'il manque de la marchandise' : 'plus que prévu'}
                    </span>
                  )}
                </div>
              )}
              {/* Garde-fou (09/10/2026) : valider un comptage inférieur au parc SORT des pièces du parc comme manquantes.
                  Un rebut déclaré par erreur comme « état des stocks » viderait ainsi le parc. */}
              {d.type === 'etat_stock' && l.source === 'parc' && ecart !== null && ecart < 0 && (
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--danger)', fontWeight: 600 }}>
                  <AlertTriangle size={12} style={{ verticalAlign: 'middle' }} /> Valider sortira {-ecart} pièce(s) du parc comme
                  manquantes. S'il s'agit d'un rebut ou d'une casse, renvoyez : le chef doit utiliser « Rebut / casse ».
                </div>
              )}
              {d.type === 'rebut' && (
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--tx-2)' }}>
                  {l.reference === 'produit_fini'
                    ? `Poteau fini au parc : valider sortira ${formatQuantite(l.quantite)} pièce(s) du parc (lots les plus anciens).`
                    : 'Perdu à la fabrication : aucun poteau ne sort du parc ; la perte est enregistrée.'}
                </div>
              )}
              {ecart === null && l.stockTheorique !== null && l.stockTheorique !== undefined && (
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--tx-3)' }}>
                  {l.source === 'parc' ? 'Au parc' : 'En stock'} avant cette déclaration :{' '}
                  <strong style={{ color: 'var(--tx-1)' }}>{l.stockTheorique}</strong>
                </div>
              )}

              {/* Matières que la validation va débiter (10/10/2026) : nomenclature × pièces déclarées. */}
              {Array.isArray(l.consommation) && (
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--tx-2)' }}>
                  {l.consommation.length === 0 ? (
                    <span style={{ color: 'var(--danger)', fontWeight: 600 }}>
                      <AlertTriangle size={12} style={{ verticalAlign: 'middle' }} /> Aucune nomenclature : aucune matière ne sera débitée.
                    </span>
                  ) : (
                    <>
                      <div style={{ color: 'var(--tx-3)' }}>Valider débitera ces matières :</div>
                      {l.consommation.map((c: any) => {
                        const apres = c.stockAvant - c.quantite;
                        return (
                          <div key={c.code} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                            <span>{c.nom}</span>
                            <span style={{ fontFamily: 'monospace', color: apres < 0 ? 'var(--danger)' : undefined }}>
                              −{c.quantite.toLocaleString('fr-FR')} {c.unite} → reste {apres.toLocaleString('fr-FR')}
                            </span>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              )}

              {/* Le commentaire de l'intervenant n'était jamais affiché : c'est
                  pourtant là qu'il explique un écart ou une casse. */}
              {l.commentaire && (
                <div style={{ marginTop: 4, fontSize: 12, fontStyle: 'italic', color: 'var(--tx-2)' }}>
                  « {l.commentaire} »
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, marginTop: 4, fontSize: 12, color: 'var(--tx-3)' }}>
                {l.reference && <span>Réf. {l.reference}</span>}
                {l.pieceJointeUrl && (
                  <a href={l.pieceJointeUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--pr-1)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <ImageIcon size={13} /> Voir la photo
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="ds-btn ds-btn-g" onClick={onRenvoyer} disabled={pending}>
          <RotateCcw size={14} /> Renvoyer pour correction
        </button>
        <button className="ds-btn ds-btn-p" onClick={onValider} disabled={pending}>
          <CheckCircle2 size={14} /> Valider
        </button>
      </div>
    </div>
  );
}


function formatJour(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}
function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function formatQuantite(q: unknown): string {
  const s = String(q ?? '');
  const n = Number(s);
  return Number.isFinite(n) ? n.toLocaleString('fr-FR') : s;
}


/** Renvoi motivé au chantier — le motif s'affiche dans l'historique de l'intervenant. */
export function RenvoyerDeclarationDialog({ declaration, onClose, onDone }: {
  declaration: any;
  onClose: () => void;
  onDone: () => void;
}) {
  const [motif, setMotif] = useState('');
  const renvoyer = useRenvoyerDeclaration();

  const submit = () => {
    if (motif.trim().length < 5) return;
    renvoyer.mutate({ id: declaration.id, motif: motif.trim() }, { onSuccess: onDone });
  };

  return (
    <Modal onClose={onClose}>
      <div className="ds-panel" style={{ maxWidth: 480, width: '100%', padding: 20 }}>
        <ModalHeader titre={`Renvoyer la déclaration à ${declaration.intervenant?.nom ?? 'l\'intervenant'}`} onClose={onClose} />
        <p style={{ fontSize: 14, color: 'var(--tx-2)', marginBottom: 12 }}>
          Le motif sera affiché à l'intervenant dans son historique. Il pourra créer une rectificative.
        </p>
        <label>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2)', marginBottom: 4 }}>Motif (min 5 chars)</div>
          <textarea
            value={motif} onChange={(e) => setMotif(e.target.value)} rows={3}
            placeholder="ex. Quantité produite semble sous-évaluée par rapport au bon de commande."
            style={{
              width: '100%', padding: '8px 12px', border: '1px solid var(--bd-2)', borderRadius: 8,
              fontSize: 14, color: 'var(--tx-1)', background: 'var(--bg-1)', resize: 'vertical', fontFamily: 'inherit',
            }}
          />
        </label>
        <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button className="ds-btn ds-btn-p" onClick={submit} disabled={motif.trim().length < 5 || renvoyer.isPending}>
            {renvoyer.isPending ? 'Envoi…' : 'Confirmer le renvoi'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
